import React, { useState, useEffect, useRef } from 'react';
import { Camera, Check } from 'lucide-react';
import { LEGAL_LINKS } from '../data/legalLinks';
import './Profile.css';

function applyCartoonFilter(imgEl) {
  return new Promise((resolve) => {
    const SIZE = 320;
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');

    // Circular clip
    ctx.save();
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.clip();

    // Draw source image (cover crop)
    const s = Math.min(imgEl.naturalWidth, imgEl.naturalHeight);
    const sx = (imgEl.naturalWidth - s) / 2;
    const sy = (imgEl.naturalHeight - s) / 2;
    ctx.drawImage(imgEl, sx, sy, s, s, 0, 0, SIZE, SIZE);

    // Boost contrast + saturation via ImageData
    const imageData = ctx.getImageData(0, 0, SIZE, SIZE);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
      // Convert to HSL-ish: boost saturation & contrast
      let r = d[i] / 255, g = d[i+1] / 255, b = d[i+2] / 255;
      // Contrast (S-curve)
      r = r < 0.5 ? 2 * r * r : 1 - Math.pow(-2 * r + 2, 2) / 2;
      g = g < 0.5 ? 2 * g * g : 1 - Math.pow(-2 * g + 2, 2) / 2;
      b = b < 0.5 ? 2 * b * b : 1 - Math.pow(-2 * b + 2, 2) / 2;
      // Saturation boost
      const avg = (r + g + b) / 3;
      const SAT = 1.5;
      r = avg + (r - avg) * SAT;
      g = avg + (g - avg) * SAT;
      b = avg + (b - avg) * SAT;
      d[i]   = Math.min(255, Math.max(0, r * 255));
      d[i+1] = Math.min(255, Math.max(0, g * 255));
      d[i+2] = Math.min(255, Math.max(0, b * 255));
    }
    ctx.putImageData(imageData, 0, 0);

    // Vignette overlay
    const vg = ctx.createRadialGradient(SIZE/2, SIZE/2, SIZE*0.35, SIZE/2, SIZE/2, SIZE/2);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,0,0.45)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, SIZE, SIZE);

    ctx.restore();

    // Circular border ring
    ctx.beginPath();
    ctx.arc(SIZE/2, SIZE/2, SIZE/2 - 2, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(56,189,248,0.7)';
    ctx.lineWidth = 4;
    ctx.stroke();

    resolve(canvas.toDataURL('image/jpeg', 0.88));
  });
}

export default function Profile({ user, onProfileUpdate }) {
  const [dataMessage, setDataMessage] = useState('');
  const [linkedinOn, setLinkedinOn] = useState(false);
  const [linkedinNote, setLinkedinNote] = useState('');

  // Downloads the learner's own data as a JSON file.
  const downloadMyData = async () => {
    setDataMessage('');
    try {
      const res = await fetch(`/api/learner/export/${encodeURIComponent(user.id)}`);
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'okiru-learn-my-data.json';
      link.click();
      URL.revokeObjectURL(url);
      setDataMessage('Your data has been downloaded.');
    } catch {
      setDataMessage('Your data could not be downloaded. Please try again.');
    }
  };
  const [form, setForm] = useState({
    name: user.name || '',
    bio: '',
  });
  const [avatarUrl, setAvatarUrl] = useState('');
  const [saving, setSaving]       = useState(false);
  const [saved,  setSaved]        = useState(false);
  const [error,  setError]        = useState('');
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [avatarNote, setAvatarNote] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    fetch(`/api/learner/profile/${user.id}`)
      .then(r => r.json())
      .then(j => {
        if (!j.success) return;
        const d = j.data;
        setForm({
          name:       d.name       || '',
          bio:        d.bio        || '',
        });
        setAvatarUrl(d.avatar_url || '');
        setAvatarFailed(false);
      })
      .catch(() => {});
  }, [user.id]);

  // Is "Fill from LinkedIn" set up on this server?
  useEffect(() => {
    fetch('/api/profile/linkedin/status').then(r => r.json()).then(j => setLinkedinOn(!!j.configured)).catch(() => {});
  }, []);

  // Coming back from LinkedIn: pre-fill the form. Nothing is saved until the
  // person selects Save changes.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('linkedin');
    const failed = params.get('linkedin_error');
    if (!state && !failed) return;
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`);
    if (failed) {
      setLinkedinNote(failed === 'cancelled' ? 'You cancelled the LinkedIn sign-in. Nothing was changed.' : 'LinkedIn could not be reached. Please try again.');
      return;
    }
    fetch(`/api/profile/linkedin/result?state=${encodeURIComponent(state)}`)
      .then(r => r.json())
      .then(j => {
        if (!j.success) { setLinkedinNote(j.error || 'That LinkedIn result has expired. Please try again.'); return; }
        const d = j.data;
        const parts = [];
        if (d.name) { setForm(f => ({ ...f, name: d.name })); parts.push('name'); }
        if (d.picture) {
          const img = new Image();
          img.onload = async () => { setAvatarUrl(await applyCartoonFilter(img)); setAvatarFailed(false); };
          img.src = d.picture;
          parts.push('photo');
        }
        const emailNote = d.email && d.email.toLowerCase() !== (user.email || '').toLowerCase()
          ? ` Your LinkedIn email (${d.email}) differs from your Okiru Learn email, which was not changed.` : '';
        setLinkedinNote(`Filled from LinkedIn: ${parts.join(' and ') || 'nothing new'}. Review it, then select Save changes.${emailNote}`);
      })
      .catch(() => setLinkedinNote('Could not read the LinkedIn result. Please try again.'));
  }, [user.email]);

  async function startLinkedin() {
    setLinkedinNote('');
    try {
      const res = await fetch('/api/profile/linkedin/start', { method: 'POST' });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      window.location.href = j.url;
    } catch (err) {
      setLinkedinNote(err.message || 'Could not start the LinkedIn sign-in.');
    }
  }

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = async () => {
        const result = await applyCartoonFilter(img);
        setAvatarUrl(result);
        setAvatarFailed(false);
        setAvatarNote('New photo chosen. Select Save changes to keep it.');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/learner/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, name: form.name, bio: form.bio, avatar_url: avatarUrl || null }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      setSaved(true);
      setAvatarNote('');
      setTimeout(() => setSaved(false), 3000);
      if (onProfileUpdate) onProfileUpdate(j.data);
    } catch (err) {
      setError(err.message || 'Save failed');
    }
    setSaving(false);
  }

  const initials = user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h2 className="profile-title">My profile</h2>
        <p className="profile-sub">Manage your name, bio and photo.</p>
      </div>

      <form className="profile-form" onSubmit={handleSave}>
        {/* Avatar */}
        <div className="profile-avatar-section">
          <div className="profile-avatar-wrap">
            {avatarUrl && !avatarFailed
              ? <img src={avatarUrl} alt="Your current profile photo" className="profile-avatar-img" onError={() => setAvatarFailed(true)} />
              : null}
            {(!avatarUrl || avatarFailed) && <div className="profile-avatar-initials" role="img" aria-label={`Your initials, ${initials}`}>{initials}</div>}
          </div>
          <button type="button" className="btn btn-ghost profile-avatar-btn" onClick={() => fileRef.current && fileRef.current.click()}>
            <Camera size={16} strokeWidth={1.75} aria-hidden="true" /> Change profile photo
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="profile-file-input" onChange={handleFile} tabIndex={-1} aria-label="Choose a profile photo file" />
          <div className="profile-avatar-hint" id="profile-avatar-hint">
            The photo is given an illustrated effect automatically.
          </div>
          {linkedinOn && (
            <div className="profile-linkedin">
              <button type="button" className="btn btn-ghost" onClick={startLinkedin}>Fill from LinkedIn</button>
              <p className="profile-hint">LinkedIn shares your name and photo only if you approve it. Nothing is saved until you select Save changes.</p>
            </div>
          )}
          <p className="profile-avatar-note" role="status">{linkedinNote || avatarNote}</p>
        </div>

        {/* Fields */}
        <div className="profile-fields">
          <div className="profile-field">
            <label className="profile-label" htmlFor="profile-name">Full name</label>
            <input id="profile-name" className="profile-input" autoComplete="name" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>

          <div className="profile-field">
            <label className="profile-label" htmlFor="profile-bio">Bio</label>
            <textarea id="profile-bio" className="profile-input profile-textarea" rows={3}
              placeholder="Tell us a bit about yourself"
              value={form.bio}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
            />
          </div>

          <div className="profile-field">
            <label className="profile-label" htmlFor="profile-company">Company</label>
            <input id="profile-company" className="profile-input" autoComplete="organization" value={user.company || ''} readOnly aria-describedby="profile-fixed-hint" />
          </div>

          <div className="profile-field">
            <label className="profile-label" htmlFor="profile-email">Email</label>
            <input id="profile-email" type="email" className="profile-input" autoComplete="email" value={user.email || ''} readOnly aria-describedby="profile-fixed-hint" />
          </div>
          <p className="profile-hint" id="profile-fixed-hint">Company and email cannot be changed here.</p>
        </div>

        {error && <p className="profile-error" role="alert">{error}</p>}

        <div className="profile-actions">
          <button type="submit" className="btn btn-primary profile-save-btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <p className="profile-saved" role="status">{saved ? <><Check size={16} strokeWidth={1.75} aria-hidden="true" /> Profile saved.</> : ''}</p>
        </div>
      </form>

      <section className="profile-data" aria-labelledby="profile-data-heading">
        <h3 id="profile-data-heading" className="profile-data-title">Your data</h3>
        <p className="profile-hint">
          You can download everything Okiru Learn holds about you, or ask for it to be deleted. See the{' '}
          <a href={LEGAL_LINKS[0].href} target="_blank" rel="noopener noreferrer">Privacy Policy<span className="sr-only"> (opens in a new tab)</span></a>{' '}
          and the{' '}
          <a href={LEGAL_LINKS[3].href} target="_blank" rel="noopener noreferrer">Data Deletion policy<span className="sr-only"> (opens in a new tab)</span></a>.
        </p>
        <button type="button" className="btn btn-ghost" onClick={downloadMyData}>Download my data</button>
        <p className="profile-hint" role="status">{dataMessage}</p>
      </section>
    </div>
  );
}
